import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { getLocale } from "src/utils/locale";
import environment from "src/utils/environment";

const Sentry = window.require("@sentry/electron");
const { app } = window.require("@electron/remote");
const path = window.require("path");
const appName = environment.appName;

const supportedLocales = {
  cs: true,
  en: true,
  de: true,
  es: true,
  fi: true,
  fr: true,
  it: true,
  ja: true,
  nl: true,
  no: true,
  pl: true,
  "pt-BR": true,
  ru: true,
  sv: true,
  tr: environment.isBibino,
  fil: environment.isBarkio,
  hu: true,
  id: environment.isBibino,
  ko: environment.isBarkio,
  "ro-RO": true,
  uk: environment.isBibino
};

export default async function setupI18n() {
  const appPath = app.getAppPath();

  function getPathToStrings(locale) {
    return path.join(appPath, appName, `locales/${locale}/strings.json`);
  }

  function getPathToPlurals(locale) {
    return path.join(appPath, appName, `locales/${locale}/plurals.json`);
  }

  function getStringFile(locale) {
    try {
      return window.require(getPathToStrings(locale));
    } catch (err) {
      Sentry.captureMessage(
        `String file for lang ${locale.toUpperCase()} has unexpected token`
      );
      return window.require(getPathToStrings("en"));
    }
  }

  function getPluralFile(locale) {
    try {
      return window.require(getPathToPlurals(locale));
    } catch (err) {
      Sentry.captureMessage(
        `Plural file for lang ${locale.toUpperCase()} has unexpected token`
      );
      return window.require(getPathToPlurals("en"));
    }
  }

  let locale = getLocale().split("-")[0];
  // nb stands for Norwegian Bokmål
  // no stands for Norwegian
  // it should be the same language

  if (locale === "nb") locale = "no";
  if (locale === "pt") locale = "pt-BR";
  if (locale === "ro") locale = "ro-RO";

  if (!supportedLocales[locale]) locale = "en";

  // Strings generated by stringsgen tool
  const strings = getStringFile(locale);

  // Manually created plural locales
  const plurals = getPluralFile(locale);

  // i18next setup
  i18n.use(initReactI18next).init({
    lng: locale,
    fallbackLng: "en",
    resources: { [locale]: { default: strings, plurals } },
    ns: ["default", "plurals"],
    defaultNS: "default",
    fallbackNS: ["plurals"],
    debug: true,
    interpolation: {
      escapeValue: false // not needed for react!!
    },
    keySeparator: "@",
    react: {
      useSuspense: true
    }
  });
}

export async function setupI18nInBrowser() {
  async function getStrings(locale) {
    const res = await import(
      `../browserSpecific/${appName}/locales/${locale}/strings.json`
    );
    return res.default;
  }

  async function getPlurals(locale) {
    const res = await import(
      `../browserSpecific/${appName}/locales/${locale}/plurals.json`
    );
    return res.default;
  }

  let locale = getLocale().split("-")[0];
  // nb stands for Norwegian Bokmål
  // no stands for Norwegian
  // it should be the same language

  if (locale === "nb") locale = "no";
  if (locale === "pt") locale = "pt-BR";
  if (locale === "ro") locale = "ro-RO";

  if (!supportedLocales[locale]) locale = "en";

  // Strings generated by stringsgen tool
  const strings = await getStrings(locale);

  // Manually created plural locales
  const plurals = await getPlurals(locale);

  // i18next setup
  i18n.use(initReactI18next).init({
    lng: locale,
    fallbackLng: "en",
    resources: { [locale]: { default: strings, plurals } },
    ns: ["default", "plurals"],
    defaultNS: "default",
    fallbackNS: ["plurals"],
    debug: true,
    interpolation: {
      escapeValue: false // not needed for react!!
    },
    keySeparator: "@",
    react: {
      useSuspense: true
    }
  });
}
